<!--
  ImageCropper.svelte — Canvas-based square image cropping tool.

  Accepts a source image (File or Blob), renders it in a canvas,
  and lets the user drag a square crop area over the image.
  Dispatches a `crop` event with the cropped Blob when "Apply" is clicked.

  Usage:
    <ImageCropper {sourceFile} oncrop={handleCrop} oncancel={handleCancel} />
-->
<script lang="ts">
	import { onMount, onDestroy, tick } from 'svelte';
	import { Button, Spinner } from 'flowbite-svelte';
	import { t } from '../i18n';

	
	interface Props {
		/** The source image file to crop. */
		sourceFile: File | Blob;
		/** Callback fired with the cropped image Blob. */
		oncrop?: (_blob: Blob) => void;
		/** Callback fired when the user cancels cropping. */
		oncancel?: () => void;
	}

	let { sourceFile, oncrop, oncancel }: Props = $props();

	/** The canvas element for rendering. */
	let canvas: HTMLCanvasElement = $state(undefined)!;
	/** Container div for sizing calculations. */
	let container: HTMLDivElement = $state(undefined)!;
	/** The loaded source image. */
	let img: HTMLImageElement | null = $state(null);
	/** Whether the image is still loading. */
	let loading = $state(true);

	// ── Display & crop state ──────────────────────────────────────

	/** Scale factor from the source image to the displayed canvas. */
	let scale = 1;
	/** Displayed image width on canvas. */
	let displayW = 0;
	/** Displayed image height on canvas. */
	let displayH = 0;
	/** Crop square position and size in *display* coordinates. */
	let cropX = 0;
	let cropY = 0;
	let cropSize = 0;

	/** Whether we're currently dragging the crop area. */
	let dragging = false;
	/** Pointer offset inside the crop area when drag started. */
	let dragOffsetX = 0;
	let dragOffsetY = 0;

	/** Whether we're resizing from a corner handle. */
	let resizing = false;
	/** Which corner is being resized: 'tl' | 'tr' | 'bl' | 'br'. */
	let resizeCorner = '';

	/** Whether we're exporting the crop result. */
	let exporting = $state(false);

	// ── Constants ─────────────────────────────────────────────────

	/** Size of the corner drag handles in pixels. */
	const HANDLE_SIZE = 12;
	/** Canvas max dimension to fit neatly in a modal. */
	const MAX_CANVAS = 460;

	// ── Lifecycle ─────────────────────────────────────────────────

	onMount(() => {
		loadImage();
		window.addEventListener('resize', fitCanvas);
	});

	onDestroy(() => {
		window.removeEventListener('resize', fitCanvas);
	});

	/**
	 * Load the source file into an HTMLImageElement.
	 */
	function loadImage(): void {
		const url = URL.createObjectURL(sourceFile);
		const image = new Image();
		image.onload = async () => {
			img = image;
			loading = false;
			/* Wait for Svelte to render the {#if img} branch so
			   the <canvas> element exists before we try to draw. */
			await tick();
			fitCanvas();
		};
		image.onerror = () => {
			loading = false;
		};
		image.src = url;
	}

	/**
	 * Size the canvas to fit the container and compute the display
	 * scaling so the full image is visible.  Initialises the crop
	 * square to the largest centred square that fits the image.
	 */
	function fitCanvas(): void {
		if (!img || !canvas) return;

		const maxDim = Math.min(MAX_CANVAS, container?.clientWidth ?? MAX_CANVAS);

		/* Fit-contain the image inside maxDim × maxDim. */
		const imgAspect = img.naturalWidth / img.naturalHeight;
		/** Choose landscape or portrait scaling based on aspect ratio. */
		if (imgAspect >= 1) {
			displayW = maxDim;
			displayH = maxDim / imgAspect;
		} else {
			displayH = maxDim;
			displayW = maxDim * imgAspect;
		}

		scale = displayW / img.naturalWidth;
		canvas.width = displayW;
		canvas.height = displayH;

		/* Initial crop: largest centred square. */
		cropSize = Math.min(displayW, displayH) * 0.85;
		cropX = (displayW - cropSize) / 2;
		cropY = (displayH - cropSize) / 2;

		draw();
	}

	// ── Drawing ───────────────────────────────────────────────────

	/**
	 * Render the image, dim the outside area, draw the crop square
	 * and corner handles.
	 */
	function draw(): void {
		if (!img || !canvas) return;
		const ctx = canvas.getContext('2d');
		/** Bail out if 2D context is unavailable. */
		if (!ctx) return;

		/* Clear and draw the image. */
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(img, 0, 0, displayW, displayH);

		/* Dim area outside the crop. */
		ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
		/* Top strip */
		ctx.fillRect(0, 0, canvas.width, cropY);
		/* Bottom strip */
		ctx.fillRect(0, cropY + cropSize, canvas.width, canvas.height - cropY - cropSize);
		/* Left strip */
		ctx.fillRect(0, cropY, cropX, cropSize);
		/* Right strip */
		ctx.fillRect(cropX + cropSize, cropY, canvas.width - cropX - cropSize, cropSize);

		/* Crop border */
		ctx.strokeStyle = '#ffffff';
		ctx.lineWidth = 2;
		ctx.strokeRect(cropX, cropY, cropSize, cropSize);

		/* Rule-of-thirds grid lines */
		ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
		ctx.lineWidth = 1;
		const third = cropSize / 3;
		/** Draw the rule-of-thirds grid lines inside the crop area. */
		for (let i = 1; i <= 2; i++) {
			/* Vertical */
			ctx.beginPath();
			ctx.moveTo(cropX + third * i, cropY);
			ctx.lineTo(cropX + third * i, cropY + cropSize);
			ctx.stroke();
			/* Horizontal */
			ctx.beginPath();
			ctx.moveTo(cropX, cropY + third * i);
			ctx.lineTo(cropX + cropSize, cropY + third * i);
			ctx.stroke();
		}

		/* Corner handles */
		ctx.fillStyle = '#ffffff';
		const hs = HANDLE_SIZE;
		const corners = [
			[cropX - hs / 2, cropY - hs / 2],
			[cropX + cropSize - hs / 2, cropY - hs / 2],
			[cropX - hs / 2, cropY + cropSize - hs / 2],
			[cropX + cropSize - hs / 2, cropY + cropSize - hs / 2]
		];
		/** Render a small square handle at each crop corner. */
		for (const [cx, cy] of corners) {
			ctx.fillRect(cx, cy, hs, hs);
		}
	}

	// ── Interaction ───────────────────────────────────────────────

	/**
	 * Convert a mouse/touch event to canvas-relative coordinates.
	 */
	function canvasCoords(e: MouseEvent | TouchEvent): { x: number; y: number } {
		const rect = canvas.getBoundingClientRect();
		const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
		const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
		return {
			x: clientX - rect.left,
			y: clientY - rect.top
		};
	}

	/**
	 * Determine if a point is over one of the four corner handles.
	 * Returns the corner id or empty string.
	 */
	function hitCorner(x: number, y: number): string {
		const hs = HANDLE_SIZE + 4; /* slightly generous hit area */
		if (Math.abs(x - cropX) < hs && Math.abs(y - cropY) < hs) return 'tl';
		if (Math.abs(x - (cropX + cropSize)) < hs && Math.abs(y - cropY) < hs) return 'tr';
		if (Math.abs(x - cropX) < hs && Math.abs(y - (cropY + cropSize)) < hs) return 'bl';
		if (Math.abs(x - (cropX + cropSize)) < hs && Math.abs(y - (cropY + cropSize)) < hs) return 'br';
		return '';
	}

	/**
	 * Determine if a point is inside the crop rectangle.
	 */
	function insideCrop(x: number, y: number): boolean {
		return x >= cropX && x <= cropX + cropSize && y >= cropY && y <= cropY + cropSize;
	}

	/**
	 * Handle pointer down — start drag or resize.
	 */
	function handlePointerDown(e: MouseEvent | TouchEvent): void {
		const { x, y } = canvasCoords(e);
		const corner = hitCorner(x, y);
		/** Begin a resize if pointer is on a corner handle, or a drag if inside the crop. */
		if (corner) {
			resizing = true;
			resizeCorner = corner;
			e.preventDefault();
		} else if (insideCrop(x, y)) {
			dragging = true;
			dragOffsetX = x - cropX;
			dragOffsetY = y - cropY;
			e.preventDefault();
		}
	}

	/**
	 * Handle pointer move — update crop position/size.
	 */
	function handlePointerMove(e: MouseEvent | TouchEvent): void {
		if (!dragging && !resizing) {
			/* Update cursor based on hover target. */
			const { x, y } = canvasCoords(e);
			const corner = hitCorner(x, y);
			if (corner === 'tl' || corner === 'br') canvas.style.cursor = 'nwse-resize';
			else if (corner === 'tr' || corner === 'bl') canvas.style.cursor = 'nesw-resize';
			else if (insideCrop(x, y)) canvas.style.cursor = 'move';
			else canvas.style.cursor = 'default';
			return;
		}

		e.preventDefault();
		const { x, y } = canvasCoords(e);
		const minSize = 32;

		/** Reposition the crop area within display bounds while dragging. */
		if (dragging) {
			cropX = Math.max(0, Math.min(displayW - cropSize, x - dragOffsetX));
			cropY = Math.max(0, Math.min(displayH - cropSize, y - dragOffsetY));
		}

		/** Resize the crop square from whichever corner is being dragged. */
		if (resizing) {
			/* Resize while keeping the square aspect ratio. */
			let newSize = cropSize;
			let newX = cropX;
			let newY = cropY;

			if (resizeCorner === 'br') {
				newSize = Math.max(minSize, Math.min(x - cropX, y - cropY, displayW - cropX, displayH - cropY));
			} else if (resizeCorner === 'bl') {
				const dx = cropX + cropSize - x;
				const dy = y - cropY;
				newSize = Math.max(minSize, Math.min(dx, dy, cropX + cropSize, displayH - cropY));
				newX = cropX + cropSize - newSize;
			} else if (resizeCorner === 'tr') {
				const dx = x - cropX;
				const dy = cropY + cropSize - y;
				newSize = Math.max(minSize, Math.min(dx, dy, displayW - cropX, cropY + cropSize));
				newY = cropY + cropSize - newSize;
			} else if (resizeCorner === 'tl') {
				const dx = cropX + cropSize - x;
				const dy = cropY + cropSize - y;
				newSize = Math.max(minSize, Math.min(dx, dy, cropX + cropSize, cropY + cropSize));
				newX = cropX + cropSize - newSize;
				newY = cropY + cropSize - newSize;
			}

			cropX = Math.max(0, newX);
			cropY = Math.max(0, newY);
			cropSize = newSize;
		}

		draw();
	}

	/**
	 * Handle pointer up — stop drag/resize.
	 */
	function handlePointerUp(): void {
		dragging = false;
		resizing = false;
	}

	// ── Export ─────────────────────────────────────────────────────

	/**
	 * Extract the cropped region from the source image and emit
	 * a `crop` event with the result as a PNG Blob.
	 */
	async function applyCrop(): Promise<void> {
		if (!img) return;
		exporting = true;

		/* Convert display coords back to source image coords. */
		const sx = cropX / scale;
		const sy = cropY / scale;
		const sSize = cropSize / scale;

		/* Draw the cropped area onto an offscreen canvas. */
		const outCanvas = document.createElement('canvas');
		outCanvas.width = 512;
		outCanvas.height = 512;
		const ctx = outCanvas.getContext('2d');
		/** Bail out if the offscreen 2D context is unavailable. */
		if (!ctx) return;

		ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, 512, 512);

		outCanvas.toBlob(
			(blob) => {
				exporting = false;
				if (blob) oncrop?.(blob);
			},
			'image/png',
			0.92
		);
	}
</script>

<div class="image-cropper" bind:this={container}>
	{#if loading}
		<div class="flex items-center justify-center py-12">
			<Spinner size="8" />
		</div>
	{:else if img}
		<canvas
			bind:this={canvas}
			class="mx-auto block rounded border border-gray-300 dark:border-gray-600"
			onmousedown={handlePointerDown}
			onmousemove={handlePointerMove}
			onmouseup={handlePointerUp}
			onmouseleave={handlePointerUp}
			ontouchstart={handlePointerDown}
			ontouchmove={handlePointerMove}
			ontouchend={handlePointerUp}
		></canvas>

		<div class="mt-4 flex justify-end gap-2">
			<Button color="alternative" size="sm" onclick={() => oncancel?.()}>{$t("Cancel")}</Button>
			<Button size="sm" onclick={applyCrop} disabled={exporting}>
				{#if exporting}
					<Spinner size="4" class="mr-2" />
				{/if}
				<i class="fa-solid fa-crop me-2"></i>{$t("Apply Crop")}
			</Button>
		</div>
	{:else}
		<p class="py-4 text-center text-red-500">{$t("Failed to load image.")}</p>
	{/if}
</div>

<style>
	.image-cropper {
		user-select: none;
		-webkit-user-select: none;
	}
</style>
